import { useConfirmation } from '../context/ConfirmationContext';
import React, { useState, useEffect, useCallback } from 'react';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import { GlassCard, Button, Badge, Modal, InputField, SectionHeader } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

interface RefundPolicy {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  downPaymentRefundPct: number;
  installmentRefundPct: number;
  processingFeeFlat: number;
  processingFeePct: number;
  fullRefundDays: number | null;
  partialRefundDays: number | null;
  noRefundAfterDays: number | null;
  allowRefundStages: string;
  requireApproval: boolean;
  autoProcessRefund: boolean;
  notes: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

const RefundPolicies: React.FC = () => {
  const { addToast } = useToast();
  const { confirmAction } = useConfirmation();
  const [policies, setPolicies] = useState<RefundPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<RefundPolicy | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    downPaymentRefundPct: 100,
    installmentRefundPct: 100,
    processingFeeFlat: 0,
    processingFeePct: 0,
    fullRefundDays: 30,
    partialRefundDays: 60,
    noRefundAfterDays: 90,
    allowRefundStages: 'INQUIRY,RESERVATION,CONTRACT_SIGNING',
    requireApproval: false,
    autoProcessRefund: false,
    notes: '',
  });

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.refunds.policies.list();
      setPolicies(data);
    } catch (err: any) {
      addToast('error', 'Failed to load refund policies');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const openCreateModal = () => {
    setEditingPolicy(null);
    setFormData({
      name: '',
      description: '',
      downPaymentRefundPct: 100,
      installmentRefundPct: 100,
      processingFeeFlat: 0,
      processingFeePct: 0,
      fullRefundDays: 30,
      partialRefundDays: 60,
      noRefundAfterDays: 90,
      allowRefundStages: 'INQUIRY,RESERVATION,CONTRACT_SIGNING',
      requireApproval: false,
      autoProcessRefund: false,
      notes: '',
    });
    setShowModal(true);
  };

  const openEditModal = (policy: RefundPolicy) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name,
      description: policy.description || '',
      downPaymentRefundPct: policy.downPaymentRefundPct,
      installmentRefundPct: policy.installmentRefundPct,
      processingFeeFlat: policy.processingFeeFlat,
      processingFeePct: policy.processingFeePct,
      fullRefundDays: policy.fullRefundDays || 30,
      partialRefundDays: policy.partialRefundDays || 60,
      noRefundAfterDays: policy.noRefundAfterDays || 90,
      allowRefundStages: policy.allowRefundStages,
      requireApproval: policy.requireApproval,
      autoProcessRefund: policy.autoProcessRefund,
      notes: policy.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        addToast('error', 'Policy name is required');
        return;
      }

      if (editingPolicy) {
        await api.refunds.policies.update(editingPolicy.id, formData);
        addToast('success', 'Refund policy updated successfully');
      } else {
        await api.refunds.policies.create(formData);
        addToast('success', 'Refund policy created successfully');
      }

      setShowModal(false);
      fetchPolicies();
    } catch (err: any) {
      addToast('error', err.message || 'Failed to save refund policy');
    }
  };

  const handleToggle = async (policy: RefundPolicy) => {
    try {
      await api.refunds.policies.toggle(policy.id);
      addToast('success', `Policy ${policy.isActive ? 'deactivated' : 'activated'}`);
      fetchPolicies();
    } catch (err: any) {
      addToast('error', 'Failed to toggle policy');
    }
  };

  const handleDelete = async (policy: RefundPolicy) => {
    await confirmAction({ title: `Delete ${policy.name}?`, description: 'This permanently removes the refund policy. It will no longer be available for refund calculations. This cannot be undone.', confirmLabel: 'Delete policy', successTitle: 'Refund policy deleted', errorTitle: 'Policy not deleted', action: async () => {
      await api.refunds.policies.delete(policy.id); fetchPolicies();
    }});
  };

  return (
    <>
      <TopBar title="Refund Policies" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SectionHeader title="Refund Policies" icon="solar:wallet-money-bold-duotone" />
          <div style={{ flex: 1 }} />
          <Button variant="primary" icon="solar:add-circle-bold-duotone" onClick={openCreateModal}>
            Create Policy
          </Button>
        </div>

        {/* Policies Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            Loading policies...
          </div>
        ) : policies.length === 0 ? (
          <GlassCard style={{ textAlign: 'center', padding: '40px' }}>
            <Icon icon="solar:wallet-money-bold-duotone" width={48} style={{ color: 'var(--text-faint)', marginBottom: '12px' }} />
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No refund policies yet</div>
            <Button variant="primary" size="sm" icon="solar:add-circle-bold-duotone" onClick={openCreateModal} style={{ marginTop: '16px' }}>
              Create First Policy
            </Button>
          </GlassCard>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '16px' }}>
            {policies.map((policy) => (
              <GlassCard key={policy.id} style={{ position: 'relative' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px', fontWeight: 700 }}>
                        {policy.name}
                      </h3>
                      <Badge variant={policy.isActive ? 'available' : 'default'}>
                        {policy.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {policy.description && (
                      <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.4 }}>
                        {policy.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Refund Percentages */}
                <div style={{ 
                  background: 'var(--bg-elevated)', 
                  borderRadius: 'var(--radius-md)', 
                  padding: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                        Down Payment
                      </div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700 }}>
                        {policy.downPaymentRefundPct}%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                        Installments
                      </div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700 }}>
                        {policy.installmentRefundPct}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time Rules */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>
                    Time-Based Rules
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                    {policy.fullRefundDays && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Full Refund</span>
                        <span style={{ color: '#10B981', fontWeight: 600 }}>Within {policy.fullRefundDays} days</span>
                      </div>
                    )}
                    {policy.partialRefundDays && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Partial Refund</span>
                        <span style={{ color: '#F59E0B', fontWeight: 600 }}>Within {policy.partialRefundDays} days</span>
                      </div>
                    )}
                    {policy.noRefundAfterDays && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>No Refund</span>
                        <span style={{ color: '#EF4444', fontWeight: 600 }}>After {policy.noRefundAfterDays} days</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Processing Fees */}
                {(policy.processingFeeFlat > 0 || policy.processingFeePct > 0) && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>
                      Processing Fees
                    </div>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
                      {policy.processingFeeFlat > 0 && (
                        <div style={{ 
                          background: 'rgba(239,68,68,0.10)', 
                          padding: '4px 8px', 
                          borderRadius: 'var(--radius-sm)',
                          color: '#EF4444',
                          fontWeight: 600
                        }}>
                          ₱{policy.processingFeeFlat.toLocaleString()}
                        </div>
                      )}
                      {policy.processingFeePct > 0 && (
                        <div style={{ 
                          background: 'rgba(239,68,68,0.10)', 
                          padding: '4px 8px', 
                          borderRadius: 'var(--radius-sm)',
                          color: '#EF4444',
                          fontWeight: 600
                        }}>
                          {policy.processingFeePct}%
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Flags */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {policy.requireApproval && (
                    <Badge variant="reserved">Requires Approval</Badge>
                  )}
                  {policy.autoProcessRefund && (
                    <Badge variant="available">Auto Process</Badge>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', paddingTop: '12px', borderTop: '1px solid var(--border-base)' }}>
                  <Button variant="secondary" size="sm" icon="solar:pen-bold-duotone" onClick={() => openEditModal(policy)} style={{ flex: 1 }}>
                    Edit
                  </Button>
                  <Button 
                    variant={policy.isActive ? 'ghost' : 'primary'} 
                    size="sm" 
                    icon={policy.isActive ? 'solar:pause-bold-duotone' : 'solar:play-bold-duotone'}
                    onClick={() => handleToggle(policy)}
                    style={{ flex: 1 }}
                  >
                    {policy.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="danger" size="sm" icon="solar:trash-bin-trash-bold-duotone" onClick={() => handleDelete(policy)}>
                    Delete
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingPolicy ? 'Edit Refund Policy' : 'Create Refund Policy'}
          width="700px"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
            {/* Basic Info */}
            <div>
              <InputField
                label="Policy Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Refund Policy"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this policy..."
                rows={2}
                style={{
                  width: '100%',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px 12px',
                  color: 'var(--input-text)',
                  fontSize: '13px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Refund Percentages */}
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                Refund Percentages
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <InputField
                  label="Down Payment Refund %"
                  type="number"
                  value={formData.downPaymentRefundPct.toString()}
                  onChange={(e) => setFormData({ ...formData, downPaymentRefundPct: Number(e.target.value) })}
                  min="0"
                  max="100"
                />
                <InputField
                  label="Installment Refund %"
                  type="number"
                  value={formData.installmentRefundPct.toString()}
                  onChange={(e) => setFormData({ ...formData, installmentRefundPct: Number(e.target.value) })}
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Processing Fees */}
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                Processing Fees
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <InputField
                  label="Flat Fee (₱)"
                  type="number"
                  value={formData.processingFeeFlat.toString()}
                  onChange={(e) => setFormData({ ...formData, processingFeeFlat: Number(e.target.value) })}
                  min="0"
                />
                <InputField
                  label="Percentage Fee (%)"
                  type="number"
                  value={formData.processingFeePct.toString()}
                  onChange={(e) => setFormData({ ...formData, processingFeePct: Number(e.target.value) })}
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>

            {/* Time Rules */}
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                Time-Based Rules (Days)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <InputField
                  label="Full Refund Within"
                  type="number"
                  value={formData.fullRefundDays?.toString() || ''}
                  onChange={(e) => setFormData({ ...formData, fullRefundDays: e.target.value ? Number(e.target.value) : 0 })}
                  min="0"
                  placeholder="Days"
                />
                <InputField
                  label="Partial Refund Within"
                  type="number"
                  value={formData.partialRefundDays?.toString() || ''}
                  onChange={(e) => setFormData({ ...formData, partialRefundDays: e.target.value ? Number(e.target.value) : 0 })}
                  min="0"
                  placeholder="Days"
                />
                <InputField
                  label="No Refund After"
                  type="number"
                  value={formData.noRefundAfterDays?.toString() || ''}
                  onChange={(e) => setFormData({ ...formData, noRefundAfterDays: e.target.value ? Number(e.target.value) : 0 })}
                  min="0"
                  placeholder="Days"
                />
              </div>
            </div>

            {/* Allowed Stages */}
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                Allowed Refund Stages
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {[
                  { value: 'INQUIRY', label: 'Inquiry' },
                  { value: 'RESERVATION', label: 'Reservation' },
                  { value: 'CONTRACT_SIGNING', label: 'Contract Signing' },
                  { value: 'PAYMENT', label: 'Payment' },
                  { value: 'COMPLETED', label: 'Completed' },
                ].map((stage) => {
                  const selectedStages = formData.allowRefundStages.split(',').map(s => s.trim()).filter(Boolean);
                  const isChecked = selectedStages.includes(stage.value);
                  
                  return (
                    <label key={stage.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          let stages = selectedStages.filter(s => s !== stage.value);
                          if (e.target.checked) {
                            stages.push(stage.value);
                          }
                          setFormData({ ...formData, allowRefundStages: stages.join(',') });
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{stage.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Checkboxes */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.requireApproval}
                  onChange={(e) => setFormData({ ...formData, requireApproval: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Require Approval</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.autoProcessRefund}
                  onChange={(e) => setFormData({ ...formData, autoProcessRefund: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Auto Process Refund</span>
              </label>
            </div>

            {/* Notes */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or instructions..."
                rows={3}
                style={{
                  width: '100%',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px 12px',
                  color: 'var(--input-text)',
                  fontSize: '13px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '12px', borderTop: '1px solid var(--border-base)' }}>
              <Button variant="ghost" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleSave}>
                {editingPolicy ? 'Update Policy' : 'Create Policy'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default RefundPolicies;
